import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "./axiosConfig";
import { useLanguage } from "./LanguageContext"; // Import du contexte
import {
  Phone,
  Clock,
  MapPin,
  CreditCard,
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  XCircle
} from "lucide-react";

export default function MerchantPublicInfo() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const DAYS = t("days_list"); // Récupère la liste des jours traduits

  const [form, setForm] = useState({
    phone: "",
    openingHours: {}, 
    paymentMethods: [],
    locationUrl: "", // <-- Ajoute ceci
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/merchants/public/me")
      .then((res) => {
        if (res.data?.merchant) {
          const data = res.data.merchant;
          let hoursData = {};
          
          const hasNewFormat = data.openingHours && data.openingHours.monday;

          if (hasNewFormat) {
            hoursData = data.openingHours;
          } else {
            const oldOpen = data.openingHours?.open || "";
            const oldClose = data.openingHours?.close || "";
            
            DAYS.forEach(day => {
              hoursData[day.key] = {
                open: oldOpen,
                close: oldClose,
                isClosed: false 
              };
            });
          }

          setForm({
            phone: data.phone || "",
            openingHours: hoursData,
            paymentMethods: data.paymentMethods || [],
            locationUrl: data.locationUrl || "",
          });
        }
      })
      .catch((err) => {
        console.error("Erreur chargement info", err);
      });
  }, [DAYS]); // On ajoute DAYS en dépendance au cas où la langue change

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateDaySchedule = (dayKey, field, value) => {
    setForm((prev) => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [dayKey]: {
          ...prev.openingHours[dayKey],
          [field]: value
        }
      },
    }));
  };

  const addPaymentMethod = () => {
    setForm((prev) => ({
      ...prev,
      paymentMethods: [
        ...prev.paymentMethods,
        { label: "", value: "" },
      ],
    }));
  };

  const updatePaymentMethod = (index, field, value) => {
    const updated = [...form.paymentMethods];
    updated[index][field] = value;
    setForm((prev) => ({ ...prev, paymentMethods: updated }));
  };

  const removePaymentMethod = (index) => {
    const updated = [...form.paymentMethods];
    updated.splice(index, 1);
    setForm((prev) => ({ ...prev, paymentMethods: updated }));
  };

  const submit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await api.put("/merchants/me/public-info", form);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Erreur / خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] p-8 font-sans text-slate-800">
      <div className="max-w-4xl mx-auto space-y-8">

        {/* HEADER */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/merchant/dashboard")}
            className="p-2 bg-white border rounded-xl hover:bg-gray-50"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-2xl font-extrabold">
            {t("public_info")}
          </h1>
        </div>

        {/* CARD PRINCIPALE */}
        <div className="p-8 space-y-8 bg-white border border-gray-100 shadow rounded-3xl">

          {/* PHONE */}
          <Section icon={<Phone size={18} />} title={t("client_contact")}>
            <input
              className="w-full px-4 py-3 border rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-100 outline-none transition"
              placeholder={t("phone_placeholder")}
              value={form.phone}
              onChange={(e) => updateField("phone", e.target.value)}
            />
          </Section>

          <hr className="border-gray-100" />

          {/* HORAIRES */}
          <Section icon={<Clock size={18} />} title={t("opening_hours_title")}>
            <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
              {DAYS.map((day) => {
                const dayData = form.openingHours[day.key] || { open: "", close: "", isClosed: false };
                const isClosed = dayData.isClosed;

                return (
                  <div key={day.key} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border-b border-gray-200 last:border-0 hover:bg-white transition-colors gap-3">
                    
                    <div className="flex items-center gap-3 w-32">
                      <button 
                        onClick={() => updateDaySchedule(day.key, "isClosed", !isClosed)}
                        className={`p-1.5 rounded-lg transition-colors ${isClosed ? "text-red-500 bg-red-100" : "text-green-600 bg-green-100"}`}
                      >
                         {isClosed ? <XCircle size={18} /> : <CheckCircle2 size={18} />}
                      </button>
                      <span className={`font-semibold ${isClosed ? "text-gray-400" : "text-gray-800"}`}>
                        {day.label}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 flex-1">
                      {!isClosed ? (
                        <>
                          <div className="flex items-center gap-2 flex-1">
                            <span className="text-xs text-gray-500 uppercase font-bold">{t("opens_at")}</span>
                            <input
                              type="time"
                              className="px-3 py-2 border rounded-lg bg-white w-full text-sm"
                              value={dayData.open}
                              onChange={(e) => updateDaySchedule(day.key, "open", e.target.value)}
                            />
                          </div>
                          <span className="text-gray-400">-</span>
                          <div className="flex items-center gap-2 flex-1">
                            <span className="text-xs text-gray-500 uppercase font-bold">{t("closes_at")}</span>
                            <input
                              type="time"
                              className="px-3 py-2 border rounded-lg bg-white w-full text-sm"
                              value={dayData.close}
                              onChange={(e) => updateDaySchedule(day.key, "close", e.target.value)}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="flex-1 py-2 text-center bg-gray-100 rounded-lg border border-dashed border-gray-300">
                          <span className="text-sm font-medium text-gray-400">{t("closed_all_day")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
              <Calendar size={12}/> {t("tip_closed")}
            </p>
          </Section>

          <hr className="border-gray-100" />

          {/* PAYMENTS */}
          <Section icon={<CreditCard size={18} />} title={t("payment_methods_accepted")}>
            <div className="space-y-3">
              {form.paymentMethods.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    className="flex-1 px-3 py-2 border rounded-xl bg-gray-50 text-sm"
                    placeholder={t("payment_label_placeholder")}
                    value={p.label}
                    onChange={(e) => updatePaymentMethod(i, "label", e.target.value)}
                  />
                  <input
                    className="flex-1 px-3 py-2 border rounded-xl bg-gray-50 text-sm"
                    placeholder={t("payment_value_placeholder")}
                    value={p.value}
                    onChange={(e) => updatePaymentMethod(i, "value", e.target.value)}
                  />
                  <button
                    onClick={() => removePaymentMethod(i)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-xl"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}

              <button
                onClick={addPaymentMethod}
                className="flex items-center gap-2 text-sm font-bold text-[#FF3B30] hover:underline"
              >
                <Plus size={16} /> {t("add_payment_method")}
              </button>
            </div>
          </Section>

          <hr className="border-gray-100" />

          <Section icon={<MapPin size={18} />} title="Localisation (Lien Google Maps)">
            <input
              className="w-full px-4 py-3 border rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-100 outline-none transition"
              placeholder="Collez le lien Google Maps de votre boutique ici"
              value={form.locationUrl}
              onChange={(e) => updateField("locationUrl", e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">
              Astuce : Allez sur Google Maps, trouvez votre boutique et cliquez sur "Partager" pour obtenir le lien.
            </p>
          </Section>
          

          {/* FEEDBACK */}
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-xl border border-red-100 animate-pulse">
              {error}
            </div>
          )}

          {success && (
            <div className="p-3 text-sm text-green-600 bg-green-50 rounded-xl border border-green-100">
              {t("update_success")}
            </div>
          )}

          {/* SAVE */}
          <button
            onClick={submit}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-4 bg-[#FF3B30] hover:bg-red-600 text-white rounded-xl font-bold text-lg shadow-lg shadow-red-100 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={20} />
            {loading ? t("saving") : t("save_mods")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-md font-bold text-gray-700">
        <div className="text-[#FF3B30]">{icon}</div>
        {title}
      </div>
      {children}
    </div>
  );
}
